<template>
  <div id="content">
    <div class="header col-md-12">
      <div class="header-inner row align-items-center">
        <div class="col-8 left">
          <div class="title">
            {{ $t('sidebar.menuonline') }} > {{ $t('sidebar.qrcode') }}
          </div>
        </div>
        <div class="col-4 right">
          <div
            v-if="ready"
            class="action">
            <div>
              <button
                v-if="hasRoleCreate"
                class="btn btn-sm btn-light"
                @click="handleOpenCreateQRCodeForm">
                {{ $t('apps.pos.message.createnewqrcode') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="content col-md-12">

      <p v-if="!ready">{{ $t('message.loading') }}</p>

      <template v-else>
        <div class="row mb-3">
          <div class="col-md-4 ml-auto">
            <label class="d-flex d-flex align-items-center">
              <span class="mr-2">
                {{ $t('message.search') }}:
              </span>
              <base-input
                v-model="query.q"
                :placeholder="refName"
                type="text"
                clear
                @onEnter="handleSearchEnter"
                @onClear="handleSearchClear"
              />
            </label>
          </div>
        </div>

        <base-table
          :pagination="$store.state.app.pagination"
          @onPageChange="handlePageChange"
        >
          <template #thead>
            <thead>
              <tr>
                <th>{{ $t('apps.pos.label.qrcoderefid') }}</th>
                <th>{{ refName }}</th>
                <th>{{ $t('apps.pos.label.queue') }}</th>
                <th>{{ $t('apps.pos.label.qrcode') }}</th>
                <th>{{ $t('apps.pos.label.appprovider') }}</th>
                <!-- <th>{{ $t('apps.pos.label.possession') }}</th>
              <th>{{ $t('apps.pos.label.branch') }}</th> -->
                <th>{{ $t('apps.pos.label.qrcodetype') }}</th>
                <th>{{ $t('apps.pos.label.createdat') }}</th>
                <th>{{ $t('apps.pos.label.qrcodeexpire') }}</th>
                <th>{{ $t('apps.pos.label.qrcodeexpirein') }}</th>
                <th>{{ $t('apps.pos.label.qrcodestatus') }}</th>
              </tr>
            </thead>
          </template>
          <template #tbody>
            <tbody>
              <tr>
                <td
                  v-if="qrcodes.length === 0"
                  colspan="10"
                  class="text-center">
                  {{ $t('message.notfounddata') }}
                </td>
              </tr>
              <tr
                v-for="(qrcode, index) in qrcodes"
                :key="index"
              >
                <td>
                  <a
                    style="color: var(--link-color);cursor: pointer;"
                    @click="handleOpenEditQRCodeForm(qrcode)">
                    {{ qrcode.qrCodeRefId }}
                  </a>
                </td>
                <td>{{ qrcode.tableNumber }}</td>
                <td>{{ qrcode.queueNumber ? `#${qrcode.queueNumber}` : '' }}</td>
                <td>
                  <button
                    class="btn btn-sm btn-light"
                    @click="openQRCodeImageModal(qrcode)"
                  >
                    <i class="fas fa-qrcode"/>
                  </button>
                </td>
                <!-- <td>{{ qrcode.psCode }}</td>
              <td>{{ qrcode.branchName }}</td> -->
                <td>{{ $t(`apps.pos.message.appprovider${qrcode.appProvider}`) }}</td>
                <td>{{ qrcode.qrCodeTypeText }}</td>
                <td>{{ qrcode.createdAtText }}</td>
                <td>{{ qrcode.expireTimeMinsText }}</td>
                <td>{{ qrcode.expiredAtFriendlyText }}</td>
                <td>{{ qrcode.paidText }}</td>
              </tr>
            </tbody>
          </template>
        </base-table>
      </template>

    </div>

    <BaseBModalQRCodeForm
      v-if="ready"
      :modalTitle="modalTitle"
      :formType="formType"
      :isSubmitted="isSubmittedQRCodeForm"
      :branches="branches"
      :posSessions="posSessions"
      :defaultValues="defaultValues"
      :qrcode="qrcode"
      @onSubmit="handleSubmitQRCodeForm"
      @onDelete="handleDeleteQRCodeForm"
    />

    <BaseBModalQRCodeImage
      v-if="ready"
      :qrcode="qrcode"
    />

  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import sidebarModule from '../../sidebarModule'
import userPermissionMixin from '@/mixins/userPermission'
import BaseBModalQRCodeForm from '../../../base/BaseBModalQRCodeForm.vue'
import BaseBModalQRCodeImage from '../../../base/BaseBModalQRCodeImage.vue'
import { transformQRCode } from '../../../../store/modules/PosQRCode'
import { getMasterDataText } from '@/helpers'

export default {
  components: {
    BaseBModalQRCodeForm,
    BaseBModalQRCodeImage,
  },
  mixins: [userPermissionMixin],
  data () {
    return {
      ready: true,
      query: {
        page: this.$route.query.page || 1,
        q: this.$route.query.q,
      },
      isSubmittedQRCodeForm: false,
      qrcode: null,
      formType: 'create', // create, edit
      defaultValues: null,
      modalTitle: null,
    }
  },
  computed: {
    ...mapGetters({
      posSessions: 'getPosSessions',
      posSettings: 'getPosSetting',
    }),
    qrcodes () {
      return this.$store.state.posQRCode.qrcodes.map((qrcode) => {
        return {
          ...qrcode,
          qrCodeTypeText: (() => {
            if (qrcode.qrCodeType === 'one_time_use') {
              return this.$t('apps.pos.message.qrcodetypeonetimeuse')
            } else {
              return this.$t('apps.pos.message.qrcodetypereusable')
            }
          })(),
          createdAtText: this.getDateTime(qrcode.createdAt),
          expiredAtFriendlyText: (() => {
            if (qrcode.qrCodeType === 'one_time_use' && qrcode.expiredAt) {
              const diffInMinutes = this.moment(qrcode.expiredAt).diff(Date.now(), 'minutes')
              if (diffInMinutes > 0) {
                return this.$t('message.timefriendlyinminutes', { number: diffInMinutes })
              } else {
                return this.$t('message.expired')
              }
            }
            return null
          })(),
          expireTimeMinsText: qrcode.expireTimeMins ? qrcode.expireTimeMins : '',
          paidText: (() => {
            if (qrcode.qrCodeType === 'one_time_use' && qrcode.isPaid) {
              return this.$t('message.paid')
            }
            return ''
          })(),
        }
      })
    },
    branches () {
      return this.$store.state.posBranch.branches
    },
    refName () {
      return getMasterDataText(this.posSettings.table.ref_name, this.$t)
    },
  },
  beforeMount () {
    // init page
    this.initPage({
      section: 'app',
      name: this.$t('app.pos.title'),
      current: {
        app_name: 'pos',
        app_id: 1,
        module_id: 5,
        submodule_id: 1,
      },
    })
    // set sidebar ui
    sidebarModule.init(this.getObject(require('../sidebar.json')), {
      sidebarAtIdx: 1,
      submenuAtIdx: 0,
    })
  },
  async created () {
    await this.$store.dispatch('getPosBranches')
    await this.$store.dispatch('getPosSessions', {
      page: 1,
      limit: 5000,
      status: 'selling',
    })

    this.fetchData()
  },
  destroyed () {
    this.leavePage(this.$Progress)
  },
  methods: {
    async fetchData () {
      this.ready = false
      this.updateUrl()
      await this.$store.dispatch('getPosQRCodes', {
        page: this.query.page,
        limit: this.$store.state.app.limitRowsPerpage,
        q: this.query.q,
      })
      this.ready = true
      this.showFinish(this.$Progress)
    },
    handlePageChange (pageNum = 1) {
      this.query.page = pageNum
      return this.fetchData()
    },
    async handleSubmitQRCodeForm({ form, ok }) {
      this.isSubmittedQRCodeForm = true

      const data = {
        'app_provider': form.appProvider,
        'is_client_approve_access_required': form.isClientApproveAccessRequired,
        'is_client_approve_order_required': form.isClientApproveOrderRequired,
        'qrcode_type': form.qrcodeType,
        'branch_id': form.branchId,
        'table_number': form.table,
        ...((() => {
          if (form.qrcodeType === 'one_time_use') {
            return {
              ps_id: form.psId,
            }
          }
          return null
        })()),
        'expire_time_mins': form.expireTime,
        'date_code': this.moment().format('YYYYMMDD'),
      }
      const response = await this.$store.dispatch('generatePosQRCode', data)

      if (!response.success) {
        this.notify(this.$vs, {
          text: this.$t('message.somethingwentwrong'),
          color: 'warning',
          icon: 'warning',
        })
      } else {
        const newQRCode = transformQRCode(response.data)

        await this.handlePageChange(1)

        ok()
        this.isSubmittedQRCodeForm = false
        this.notify(this.$vs, {
          text: this.$t('message.newqrcodecreated'),
          color: 'success',
          icon: 'cloud',
        })

        setTimeout(() => {
          this.openQRCodeImageModal(newQRCode)
        }, 100)
      }
    },
    openQRCodeImageModal(qrcode) {
      this.qrcode = qrcode
      this.$bvModal.show('modal-qrcode-image')
    },
    async handleDeleteQRCodeForm({ qrcode, ok }) {
      this.isSubmittedQRCodeForm = true

      const qrcodeId = qrcode.id
      await this.$store.dispatch('deletePosQRCode', qrcodeId)

      this.isSubmittedQRCodeForm = false
      this.notify(this.$vs, {
        text: this.$t('message.deleted'),
        color: 'danger',
        icon: 'delete',
      })
      ok()

      await this.fetchData()
    },
    handleOpenCreateQRCodeForm() {
      const tableLists = this.posSettings.table.lists.filter((item) => item.is_active)

      this.formType = 'create'
      this.modalTitle = this.$t('apps.pos.message.createnewqrcode')
      this.defaultValues = {
        appProvider: this.posSettings.menu_online.default_app_provider,
        qrcodeType: this.posSettings.menu_online.default_qrcode_type,
        isClientApproveAccessRequired: this.posSettings.menu_online.default_is_client_approve_access_required,
        isClientApproveOrderRequired: this.posSettings.menu_online.default_is_client_approve_order_required,
        expireTime: this.posSettings.menu_online.default_qrcode_expire_min,
        branchId: this.branches.length ? this.branches[0].branchId : '',
        psId: this.posSessions.length ? this.posSessions[0].ps_id : '',
        table: tableLists.length ? tableLists[0].name : null,
      }
      this.$bvModal.show('modal-qrcode-form')
    },
    handleOpenEditQRCodeForm(qrcode) {
      this.formType = 'edit'
      this.modalTitle = (() => {
        let str = `${qrcode.qrCodeRefId} - ${this.$t('apps.pos.message.tablenumber', { ref_name: this.refName, number: qrcode.tableNumber })}`
        if (qrcode.queueNumber) {
          str += ` ${this.$t('apps.pos.message.queuenumber', { number: qrcode.queueNumber })}`
        }
        return str
      })()
      this.defaultValues = {
        appProvider: qrcode.appProvider,
        qrcodeType: qrcode.qrCodeType,
        isClientApproveAccessRequired: qrcode.clientApproveAccessRequired,
        isClientApproveOrderRequired: qrcode.clientApproveOrderRequired,
        expireTime: qrcode.expireTimeMins,
        branchId: qrcode.branchId,
        psId: qrcode.psId,
        table: qrcode.tableNumber,
      }
      this.qrcode = qrcode
      this.$bvModal.show('modal-qrcode-form')
    },
    handleSearchEnter () {
      this.query.page = undefined
      this.fetchData()
    },
    handleSearchClear () {
      this.query.q = undefined
      this.query.page = undefined
      this.fetchData()
    },
    updateUrl () {
      const query = this.cleanObject(this.query)
      this.$router.push({ query }).catch(() => {})
    },
  },
}
</script>

<style scoped>

</style>



// WEBPACK FOOTER //
// src/components/apps/pos/menuonline/QRCode.vue